import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import Autocomplete from "react-google-autocomplete";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useAuth } from "../context/AuthContext"; // Add this to access user state
import { Link } from "react-router-dom";
import {
  buildApiUrl,
  getApiBaseUrl,
  readJsonResponse,
} from "../utils/apiConfig";
import { OPEN_ACCESS_MODE } from "../config/accessMode.js";
import { useTranslation } from "react-i18next";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
// To make the app functional, please replace 'YOUR_API_KEY' with your actual Google Maps API key.
// Example: const GOOGLE_MAPS_API_KEY = 'AIzaSyB-C1...';
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY || "";
const HAS_GOOGLE_API_KEY = Boolean(
  GOOGLE_MAPS_API_KEY && GOOGLE_MAPS_API_KEY.trim(),
);

const API_BASE_URL = getApiBaseUrl();

const DownloadIcon = (props) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="lucide lucide-download"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" x2="12" y1="15" y2="3" />
  </svg>
);

const CloseIcon = (props) => (
   <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="lucide lucude-x"
  >
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

const MapPinIcon = (props) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="lucide lucide-map-pin"
  >
    <path d="M12 18s-2.5-3.5-2.5-5.5a2.5 2.5 0 0 1 5 0c0 2-2.5 5.5-2.5 5.5z" />
    <circle cx="12" cy="12" r="2.5" />
    <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z" />
  </svg>
);

const SearchIcon = (props) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="lucide lucide-search"
  >
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

const allReturnPeriods = ["2", "5", "10", "25", "50", "100"];

const MVPIDFViewerV2 = () => {
  const auth = useAuth();
  const user = auth?.user ?? null;
  const authFetch = auth?.authFetch ?? null;
  const { t } = useTranslation();
  const [trialMessage, setTrialMessage] = useState("");
  const [station, setStation] = useState(null);
  const [idfData, setIDFData] = useState([]);
  const [applyClimate2050High, setApplyClimate2050High] = useState(false);
  const [applyQc18, setApplyQc18] = useState(false);
  const [climateInfo, setClimateInfo] = useState(null);
  const [allowSubhourFallback, setAllowSubhourFallback] = useState(false);
  const [idfFallbackInfo, setIdfFallbackInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [isStationInfoVisible, setIsStationInfoVisible] = useState(false);
  const [showChart, setShowChart] = useState(false);
  const [selectedReturnPeriods, setSelectedReturnPeriods] =
    useState(allReturnPeriods);
  const [place, setPlace] = useState(null);
  const chartDataRef = useRef(null);
  const exportMenuRef = useRef(null);
  const hasGoogleApiKey = HAS_GOOGLE_API_KEY;
  const [searchMode, setSearchMode] = useState("nearest");
  const [stations, setStations] = useState([]);
  const [stationsLoading, setStationsLoading] = useState(false);
  const [stationsError, setStationsError] = useState(null);
  const [stationQuery, setStationQuery] = useState("");
  const [selectedStation, setSelectedStation] = useState(null);
  const [stationMenuOpen, setStationMenuOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [selectedProvince, setSelectedProvince] = useState("ALL");
  const [selectedCountry, setSelectedCountry] = useState("CA");
  const [usProviderMessage, setUsProviderMessage] = useState("");
  const [usLocationQuery, setUsLocationQuery] = useState("");
  const [usLatitude, setUsLatitude] = useState("");
  const [usLongitude, setUsLongitude] = useState("");
  const [usLoading, setUsLoading] = useState(false);
  const [usResolvedLocation, setUsResolvedLocation] = useState(null);
  const [trialVerificationLoading, setTrialVerificationLoading] = useState(false);
  const [usDataType, setUsDataType] = useState("intensity");
  const [usSeries, setUsSeries] = useState("ams");
  const [usEstimate, setUsEstimate] = useState("mean");
  const [usCurveMeta, setUsCurveMeta] = useState({
    dataType: "intensity",
    series: "ams",
    estimate: "mean",
  });
  // Cloud Run bugfix: the location input sometimes becomes disabled, which stops typing.
  // Force-keep it enabled.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const el = document.getElementById("location");
    if (!el) return;

    const forceEnable = () => {
      try {
        if (el.disabled) el.disabled = false;
        if (el.hasAttribute("disabled")) el.removeAttribute("disabled");
      } catch (_e) {
        // no-op
      }
    };

    forceEnable();
    const obs = new MutationObserver(() => forceEnable());
    obs.observe(el, { attributes: true, attributeFilter: ["disabled"] });
    return () => obs.disconnect();
  }, []);
  
  const trialExpired = useMemo(() => {
    const message = (trialMessage || "").toLowerCase();
    const errorMessage = (error || "").toLowerCase();
    const expiredText = t("idf.trial.expired").toLowerCase();
    return (
      message.includes(expiredText) ||
      message.includes("free trial has expired") ||
      errorMessage.includes("free trial has expired")
      
    );
  }, [trialMessage, error, t]);
  
  // We rely on `react-google-autocomplete` to load/init Places correctly.
 useEffect(() => {
  if (!hasGoogleApiKey) {
    setError(t("idf.errors.googleApiMissing"));
  }
}, [hasGoogleApiKey, t]);

  useEffect(() => {
  if (!user) {
    setTrialMessage("");
    return;
  }

  // Feedback period: hide trial/paywall banners (Stripe code unchanged)
  if (OPEN_ACCESS_MODE) {
    setTrialMessage("");
    return;
  }
  
  const role = (user.role || "").toLowerCase();
  const hasPaidSubscription = Boolean(user.stripeCustomerId || user.plan);

  // Admins never see trial-expired banner
  if (role === "admin") {
    setTrialMessage("");
    return;
  }

  // Paid non-admin users do not see trial banner
  if (user.subscriptionStatus === "active" && hasPaidSubscription) {
    setTrialMessage("");
    return;
  }

  if (user.subscriptionStatus === "trial_pending") {
    setTrialMessage(t("idf.trial.pendingVerification"));
    return;
  }

  if (user.trialEndsAt) {
    const endsAt = new Date(user.trialEndsAt);
    const now = new Date();
    const diffMs = endsAt - now;
    if (diffMs > 0) {
      const daysLeft = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      setTrialMessage(t("idf.trial.endsIn", { count: daysLeft }));
    } else {
      setTrialMessage(t("idf.trial.expired"));
    }
    } else if (user.subscriptionStatus === "trialing") {
      setTrialMessage(t("idf.trial.active"));
    } else if (user.subscriptionStatus !== "active") {
      setTrialMessage(t("idf.trial.unverified"));
    } else {
      setTrialMessage("");
    }
    }, [user, t]);
  useEffect(() => {
  if (!exportOpen) return;

  const onKeyDown = (event) => {
    if (event.key === "Escape") {
      setExportOpen(false);
    }
  };

  document.addEventListener("keydown", onKeyDown);
  return () => document.removeEventListener("keydown", onKeyDown);
  }, [exportOpen]);
  useEffect(() => {
  if (!exportOpen) return;

  const onMouseDown = (event) => {
    if (
      exportMenuRef.current &&
      !exportMenuRef.current.contains(event.target)
    ) {
      setExportOpen(false);
    }
  };

  document.addEventListener("mousedown", onMouseDown);
  return () => document.removeEventListener("mousedown", onMouseDown);
  }, [exportOpen]);
  const formatStationLabel = (station) =>
  `${station.stationName || station.name || "Unknown"} (${station.stationId})${station.provinceCode ? ` — ${station.provinceCode}` : ""}`;

  const stationOptions = useMemo(() => {
  const q = stationQuery.trim().toLowerCase();

  const base = selectedProvince === "ALL"
    ? stations
    : stations.filter((s) => (s.provinceCode || "").trim() === selectedProvince);

  if (!q) return base.slice(0, 50);

  return base
    .filter((s) => {
      const name = (s.stationName || s.name || "").toLowerCase();
      const id = String(s.stationId || "").toLowerCase();
      const prov = String(s.provinceCode || "").toLowerCase();
      return name.includes(q) || id.includes(q) || prov.includes(q);
    })
    .slice(0, 50);
}, [stations, stationQuery, selectedProvince]);

const handleStationQueryChange = (value) => {
  setStationQuery(value);
  setSelectedStation(null);
  setStationMenuOpen(true);
  setHighlightedIndex(0);
};

const handleStationSelect = (station) => {
  setSelectedStation(station);
  setStationQuery(formatStationLabel(station));
  setStationMenuOpen(false);
};

const handleStationInputKeyDown = (event) => {
  if (!stationMenuOpen || stationOptions.length === 0) return;

  if (event.key === "ArrowDown") {
    event.preventDefault();
    setHighlightedIndex((prev) => Math.min(prev + 1, stationOptions.length - 1));
  } else if (event.key === "ArrowUp") {
    event.preventDefault();
    setHighlightedIndex((prev) => Math.max(prev - 1, 0));
  } else if (event.key === "Enter") {
    event.preventDefault();
    handleStationSelect(stationOptions[highlightedIndex]);
  } else if (event.key === "Escape") {
    setStationMenuOpen(false);
  }
};

  const normalizeIdfRows = useCallback((rawRows) => {
    if (!Array.isArray(rawRows)) return [];

    return rawRows
      .map((item) => {
        const durationValue = Number(item?.duration);
        if (!Number.isFinite(durationValue) || durationValue <= 0) return null;

        const normalized = { duration: durationValue };
        let hasValues = false;

        allReturnPeriods.forEach((period) => {
          const value = Number(item?.[period]);
          if (Number.isFinite(value) && value > 0) {
            normalized[period] = value;
            hasValues = true;
          }
        });

        return hasValues ? normalized : null;
      })
      .filter(Boolean)
      .sort((a, b) => a.duration - b.duration);
  }, []);

  const handleDirectLoad = useCallback(async () => {
  if (selectedCountry !== "CA") {
    setError(t("idf.country.usComingSoon"));
    return;
  }

  if (!selectedStation?.stationId) {
    setError(t("idf.errors.selectStation"));
    return;
  }

  setError(null);
  setIDFData([]);
  setClimateInfo(null);
  setIdfFallbackInfo(null);
  setStation(selectedStation);
  setIsStationInfoVisible(true);
  setShowChart(false);
  setLoading(true);

  try {
    const climateParam = applyClimate2050High
      ? "cc_2050_high"
      : applyQc18
      ? "qc18"
      : "";

    const idfParams = new URLSearchParams({
      stationId: String(selectedStation.stationId),
      country: selectedCountry,
    });
    if (climateParam) idfParams.set("climate", climateParam);
    if (allowSubhourFallback) idfParams.set("allowSubhourFallback", "1");
    const idfUrl = `${buildApiUrl("/v2/idf/curves")}?${idfParams.toString()}`;

    const idfResponse = await (authFetch ? authFetch(idfUrl) : fetch(idfUrl));

    const idfJson = await readJsonResponse(
      idfResponse,
      "Failed to fetch IDF data.",
    );

    if (!idfResponse.ok) {
      if (
        (idfResponse.status === 402 || idfResponse.status === 403) &&
        idfJson?.code === "trial_expired"
      ) {
        setTrialMessage("Your free trial has expired. Please upgrade to continue accessing IDF curves.");
      } else if (
        (idfResponse.status === 402 || idfResponse.status === 403) &&
        idfJson?.code === "trial_pending"
      ) {
        setTrialMessage(t("idf.trial.pendingVerification"));
      }
      throw new Error(idfJson?.error || "Failed to fetch IDF data.");
    }

    setClimateInfo(idfJson?.climate || null);
    setIdfFallbackInfo(idfJson?.fallback || null);

    const processedData = idfJson.data
      .map((item) => {
        let durationInMinutes = 0;

        if (typeof item.duration === "number") {
          durationInMinutes = item.duration;
        } else {
          const durationString = String(item.duration);
          if (durationString.includes("min")) {
            durationInMinutes = parseInt(durationString.replace(" min", ""), 10);
          } else if (durationString.includes("h")) {
            durationInMinutes = parseInt(durationString.replace(" h", ""), 10) * 60;
          } else if (durationString.includes("d")) {
            durationInMinutes = parseInt(durationString.replace(" d", ""), 10) * 24 * 60;
          }
        }

        if (durationInMinutes <= 0) return null;

        const newItem = { duration: durationInMinutes };
        let hasValidData = false;

        allReturnPeriods.forEach((period) => {
          const value = parseFloat(item[period]);
          if (!isNaN(value)) {
            const durationHours = durationInMinutes / 60;
            const intensity = durationHours > 0 ? value / durationHours : null;
            if (intensity !== null && !isNaN(intensity) && isFinite(intensity)) {
              newItem[period] = intensity;
              hasValidData = true;
            }
          }
        });

        return hasValidData ? newItem : null;
      })
      .filter(Boolean);

    const sortedData = processedData.sort((a, b) => a.duration - b.duration);

    if (sortedData.length > 0) {
      setIDFData(sortedData);
      chartDataRef.current = sortedData;
      setShowChart(true);
    } else {
      setError("No valid IDF curve data could be found for this station. The data may be missing or malformed.");
      setShowChart(false);
    }
  } catch (err) {
    console.error(err);
    const apiHint = API_BASE_URL ? ` (API base: ${API_BASE_URL})` : "";
    setError(
      err.message
        ? `${err.message}${apiHint}`
        : `Unexpected error while contacting the server.${apiHint}`,
    );
  } finally {
    setLoading(false);
  }
}, [
  selectedCountry,
  selectedStation,
  authFetch,
  applyClimate2050High,
  applyQc18,
  allowSubhourFallback,
  t,
]);
 const provinceOptions = useMemo(() => {
  const codes = Array.from(
    new Set(
      stations
        .map((s) => (s.provinceCode || "").trim())
        .filter(Boolean)
    )
  ).sort();

  return ["ALL", ...codes];
  }, [stations]); 
  const handleSearch = useCallback(
    async (e) => {
      e.preventDefault();
      if (selectedCountry !== "CA") {
        setError(t("idf.country.usComingSoon"));
        return;
      }

        setError(null);
        setIDFData([]);
        setClimateInfo(null);
        setIdfFallbackInfo(null);
        setStation(null);
        setShowChart(false);
        setIsStationInfoVisible(false);
        setLoading(true);

      if (!place || !place.geometry) {
        setError(t("idf.errors.selectValidLocation"));
        setLoading(false);
        return;
      }

      const lat = place.geometry.location.lat();
      const lon = place.geometry.location.lng();
      let provinceCode = "";

      if (place.address_components) {
        for (const component of place.address_components) {
          if (component.types.includes("administrative_area_level_1")) {
            provinceCode = component.short_name;
            break;
          }
        }
      }

    try {
        const params = new URLSearchParams({
          lat: String(lat),
          lon: String(lon),
          province: provinceCode,
        });
        const nearestResponse = await fetch(
          `${buildApiUrl("/nearest-station")}?${params.toString()}`,
        );
        const nearestData = await readJsonResponse(
          nearestResponse,
          t("idf.errors.nearestStationFailed"),
        );
        if (!nearestResponse.ok) {
          throw new Error(
            nearestData?.error || t("idf.errors.nearestStationFailed"),
          );
        }
        const nearestStation = nearestData;
        setStation(nearestStation);
        setIsStationInfoVisible(true);
        console.log("Found nearest station:", nearestStation);

       const climateParam = applyClimate2050High
         ? "cc_2050_high"
         : applyQc18
           ? "qc18"
           : "";
       const idfParams = new URLSearchParams({
         stationId: String(nearestStation.stationId),
         country: selectedCountry,
       });
       if (climateParam) idfParams.set("climate", climateParam);
       if (allowSubhourFallback) idfParams.set("allowSubhourFallback", "1");
       const idfUrl = `${buildApiUrl("/v2/idf/curves")}?${idfParams.toString()}`;

       const idfResponse = await (authFetch
        ? authFetch(
            idfUrl,
          )
        : fetch(
            idfUrl,
          ));

        const idfJson = await readJsonResponse(
          idfResponse,
          t("idf.errors.idfFetchFailed"),
        );
        if (!idfResponse.ok) {
          if (
            (idfResponse.status === 402 || idfResponse.status === 403) &&
            idfJson?.code === "trial_expired"
          ) {
           setTrialMessage(t("idf.trial.expired"));
          } else if (
            (idfResponse.status === 402 || idfResponse.status === 403) &&
            idfJson?.code === "trial_pending"
          ) {
            setTrialMessage(t("idf.trial.pendingVerification"));
          }
          throw new Error(idfJson?.error || t("idf.errors.idfFetchFailed"));
        }
        setClimateInfo(idfJson?.climate || null);
        setIdfFallbackInfo(idfJson?.fallback || null);
        console.log("Raw IDF data from API:", idfJson.data);

        const processedData = idfJson.data
            .map((item, index) => {
              console.log(`Processing item ${index}:`, item);
              let durationInMinutes = 0;

              // First, check if the duration is a number.
              if (typeof item.duration === "number") {
                console.log(`- Duration is a number: ${item.duration}`);
                durationInMinutes = item.duration;
              } else {
                // If not a number, fall back to the string parsing logic.
                const durationString = String(item.duration);
                console.log(
                  `- Duration is a string: "${durationString}". Attempting to parse.`,
                );
                if (durationString.includes("min")) {
                  durationInMinutes = parseInt(
                    durationString.replace(" min", ""),
                    10,
                  );
                } else if (durationString.includes("h")) {
                  durationInMinutes =
                    parseInt(durationString.replace(" h", ""), 10) * 60;
                } else if (durationString.includes("d")) {
                  durationInMinutes =
                    parseInt(durationString.replace(" d", ""), 10) * 24 * 60;
                }
              }

              console.log(`- Converted to minutes: ${durationInMinutes}`);

          // Skip data points with non-positive duration, as they will break the log scale
              if (durationInMinutes <= 0) {
                console.log(
                  `- Skipping item ${index} due to non-positive duration.`,
                );
                return null;
              }

          const newItem = { duration: durationInMinutes };
              let hasValidData = false;

              allReturnPeriods.forEach((period) => {
                const value = parseFloat(item[period]);
                if (!isNaN(value)) {
                  const durationHours = durationInMinutes / 60;
                  const intensity =
                    durationHours > 0 ? value / durationHours : null;
                  if (
                    intensity !== null &&
                    !isNaN(intensity) &&
                    isFinite(intensity)
                  ) {
                    newItem[period] = intensity;
                    hasValidData = true;
                  }
                } else {
                  console.log(
                    `- Item ${index}: Value for return period "${period}" is not a number.`,
                  );
                }
              });

              if (!hasValidData) {
                console.log(
                  `- Skipping item ${index} because no valid numerical IDF values were found.`,
                );
                return null;
              }

          console.log(`- Successfully processed item ${index}:`, newItem);
              return newItem;
            })
            .filter(Boolean); // Filter out any null items

      const sortedData = processedData.sort(
          (a, b) => a.duration - b.duration,
        );

        console.log("Final processed and sorted data for chart:", sortedData);

          if (sortedData.length > 0) {
            setIDFData(sortedData);
            chartDataRef.current = sortedData;
            setShowChart(true);
          } else {
            setError(t("idf.errors.noValidCurveData"));
            setShowChart(false);
          }
        } catch (err) {
          console.error(err);
          const apiHint = API_BASE_URL ? ` (API base: ${API_BASE_URL})` : "";
          setError(
            err.message
              ? `${err.message}${apiHint}`
              : `${t("idf.errors.unexpectedServer")}${apiHint}`,
          );
        } finally {
          setLoading(false);
        }
      },
      [authFetch, place, applyClimate2050High, applyQc18, allowSubhourFallback, selectedCountry, t],
    );

  const handleUsLoad = useCallback(async () => {
    const latText = usLatitude.trim();
    const lonText = usLongitude.trim();
    const lat = latText !== "" ? Number(latText) : NaN;
    const lon = lonText !== "" ? Number(lonText) : NaN;
    const locationQuery = usLocationQuery.trim();
    const hasCoordinateInput = latText !== "" || lonText !== "";
    const hasBothCoordinates = latText !== "" && lonText !== "";
    const hasValidCoordinates =
      hasBothCoordinates &&
      Number.isFinite(lat) &&
      Number.isFinite(lon) &&
      lat >= -90 &&
      lat <= 90 &&
      lon >= -180 &&
      lon <= 180;

    if (!hasValidCoordinates && !locationQuery) {
      setError(t("idf.country.usNeedsInput"));
      setUsProviderMessage(t("idf.country.usNeedsCoordinates"));
      return;
    }

    if (hasCoordinateInput && !hasValidCoordinates) {
      setError(t("idf.country.invalidCoordinates"));
      setUsProviderMessage(t("idf.country.usNeedsCoordinates"));
      return;
    }

    setError(null);
    setLoading(false);
    setUsLoading(true);
    setIDFData([]);
    chartDataRef.current = null;
    setShowChart(false);
    setStation(null);
    setIsStationInfoVisible(false);
    setClimateInfo(null);
    setIdfFallbackInfo(null);
    setUsProviderMessage(t("idf.country.checking"));
    setUsResolvedLocation(null);

    try {
      const params = new URLSearchParams({ country: "US" });
      if (hasValidCoordinates) {
        params.set("lat", String(lat));
        params.set("lon", String(lon));
      }
      if (locationQuery) params.set("q", locationQuery);
      params.set("returnPeriods", allReturnPeriods.join(","));
      params.set("durations", "5,10,15,30,60,120,360,720,1440");
      params.set("dataType", usDataType);
      params.set("series", usSeries);
      params.set("estimate", usEstimate);
      const idfUrl = `${buildApiUrl("/v2/idf/curves")}?${params.toString()}`;
      const response = await (authFetch ? authFetch(idfUrl) : fetch(idfUrl));
      const payload = await readJsonResponse(response, t("idf.errors.idfFetchFailed"));

      if (!response.ok) {
        throw new Error(payload?.error || payload?.message || t("idf.errors.idfFetchFailed"));
      }

      const normalizedRows = normalizeIdfRows(payload?.data);
      const providerMessage =
        payload?.provider?.message || payload?.message || t("idf.country.usNoDataYet");
      const resolvedDataType = payload?.provider?.dataType || usDataType;
      const resolvedSeries = payload?.provider?.series || usSeries;
      const resolvedEstimate = payload?.provider?.estimate || usEstimate;
      const resolvedLat = Number(payload?.location?.lat);
      const resolvedLon = Number(payload?.location?.lon);
      const finalLat = Number.isFinite(resolvedLat) ? resolvedLat : hasValidCoordinates ? lat : null;
      const finalLon = Number.isFinite(resolvedLon) ? resolvedLon : hasValidCoordinates ? lon : null;
      const locationLabel =
        payload?.location?.label ||
        (finalLat != null && finalLon != null
          ? `US NOAA (${finalLat.toFixed(4)}, ${finalLon.toFixed(4)})`
          : `US NOAA (${locationQuery})`);

      setUsResolvedLocation({
        label: locationLabel,
        lat: finalLat,
        lon: finalLon,
      });
      setUsCurveMeta({
        dataType: resolvedDataType,
        series: resolvedSeries,
        estimate: resolvedEstimate,
      });

      if (normalizedRows.length > 0) {
        setIDFData(normalizedRows);
        chartDataRef.current = normalizedRows;
        setShowChart(true);
        setStation({
          stationId:
            finalLat != null && finalLon != null
              ? `US_${finalLat.toFixed(4)}_${finalLon.toFixed(4)}`
              : "US_NOAA_GRID",
          stationName: locationLabel,
          lat: finalLat,
          lon: finalLon,
          distance_km: 0,
        });
        setUsProviderMessage(providerMessage);
      } else {
        setUsProviderMessage(providerMessage);
      }
    } catch (err) {
      const apiHint = API_BASE_URL ? ` (API base: ${API_BASE_URL})` : "";
      setError(err?.message ? `${err.message}${apiHint}` : `${t("idf.errors.idfFetchFailed")}${apiHint}`);
      setUsProviderMessage(t("idf.country.usFetchFailed"));
    } finally {
      setUsLoading(false);
    }
  }, [
    authFetch,
    normalizeIdfRows,
    t,
    usLatitude,
    usLongitude,
    usLocationQuery,
    usDataType,
    usSeries,
    usEstimate,
  ]);

  const handleCheckboxChange = useCallback((event) => {
    const { value, checked } = event.target;
    setSelectedReturnPeriods((prev) => {
      let newPeriods;
      if (checked) {
        if (!prev.includes(value)) {
          newPeriods = [...prev, value];
        } else {
          newPeriods = prev;
        }
      } else {
        newPeriods = prev.filter((p) => p !== value);
      }
      // Sort the array numerically to ensure the legend order is correct
      return newPeriods.sort((a, b) => parseInt(a) - parseInt(b));
    });
  }, []);
  const loadStationsIfNeeded = useCallback(async () => {
  if (stations.length > 0 || stationsLoading) return;

  try {
    setStationsLoading(true);
    setStationsError(null);

    const res = await fetch(buildApiUrl("/stations"));
    const data = await readJsonResponse(res, "Failed to load stations.");

    if (!res.ok) {
      throw new Error(data?.error || "Failed to load stations.");
    }

    setStations(Array.isArray(data) ? data : []);
  } catch (err) {
    console.error(err);
    setStationsError(err.message || "Failed to load stations.");
  } finally {
    setStationsLoading(false);
  }
  }, [stations.length, stationsLoading]);

  useEffect(() => {
  if (searchMode === "direct") {
    loadStationsIfNeeded();
  }
  }, [searchMode, loadStationsIfNeeded]);

  useEffect(() => {
    if (selectedCountry === "US") {
      setLoading(false);
      setUsLoading(false);
      setUsProviderMessage(t("idf.country.usNeedsCoordinates"));
      return;
    }

    setUsProviderMessage("");
    setUsLoading(false);
    setUsResolvedLocation(null);
    setUsCurveMeta({
      dataType: "intensity",
      series: "ams",
      estimate: "mean",
    });
  }, [selectedCountry, t]);

  const formatIntensityValue = useCallback(
    (value) => {
      if (value == null || !Number.isFinite(Number(value))) return "-";
      return Number(value).toFixed(selectedCountry === "US" ? 2 : 1);
    },
    [selectedCountry],
  );

  const handleDownload = useCallback(() => {
    if (chartDataRef.current) {
      const dataStr = JSON.stringify(chartDataRef.current, null, 2);
      const dataUri =
        "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);
      const link = document.createElement("a");
      link.setAttribute("href", dataUri);
      link.setAttribute("download", `idf_data_${station.stationId}.json`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [station]);
  const handleCSVDownload = useCallback(() => {
   if (!chartDataRef.current) return;

   const headers = [
  t("idf.chart.tableDuration"),
  ...allReturnPeriods.map((p) => t("idf.chart.legend", { period: p })),
];
    const rows = chartDataRef.current.map((item) => {
      return [
        formatDurationLabel(item.duration),
        ...allReturnPeriods.map((p) => item[p] ?? ""),
      ].join(",");
    });

   const csvContent = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

   const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `idf_data_${station.stationId}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [station, t]);
  const handleGeoJSONDownload = useCallback(() => {
    if (!chartDataRef.current || !station) return;

    const features = chartDataRef.current.map((row) => ({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [station.lon, station.lat],
      },
      properties: {
        stationId: station.stationId,
        stationName: station.stationName || station.name,
        duration: row.duration,
        ...allReturnPeriods.reduce((acc, period) => {
          acc[`${period}-Year`] = row[period] ?? null;
          return acc;
        }, {}),
      },
    }));

    const geojson = {
      type: "FeatureCollection",
      features,
    };

    const blob = new Blob([JSON.stringify(geojson, null, 2)], {
      type: "application/geo+json",
    });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `idf_data_${station.stationId}.geojson`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [station]);
  const handlePdfDownload = useCallback(() => {
  if (!idfData?.length || !station) return;

  const doc = new jsPDF();
  doc.setFontSize(14);
  doc.text(t("idf.pdf.title"), 14, 16);

  doc.setFontSize(10);
  doc.text(`${t("idf.pdf.stationId")} ${station.stationId}`, 14, 24);
  doc.text(`${t("idf.pdf.station")} ${station.stationName || station.name || ""}`, 14, 30);
  doc.text(`${t("idf.pdf.generated")} ${new Date().toLocaleString()}`, 14, 36);

  const headers = [
  t("idf.pdf.durationHeader"),
  ...allReturnPeriods.map((p) => t("idf.chart.legend", { period: p })),
  ];
  const rows = idfData.map((row) => [
    formatDurationLabel(row.duration),
    ...allReturnPeriods.map((p) => formatIntensityValue(row[p])),
  ]);

  autoTable(doc, {
    startY: 42,
    head: [headers],
    body: rows,
    styles: { fontSize: 9 },
    headStyles: { fillColor: [31, 41, 55] },
  });

  doc.save(`idf_report_${station.stationId}.pdf`);
}, [idfData, station, t, formatIntensityValue]);
  const getLineColor = (period) => {
    const colors = {
       2: "#03a9f4",
      5: "#4caf50",
      10: "#ffc107",
      25: "#ff5722",
      50: "#9c27b0",
      100: "#e91e63",
    };
    return colors[period] || "#000000";
  };

  const getLineDash = (period) => {
    if (period === "2" || period === "5") {
      return null;
    }
    return "5 5";
  };

  const formatDurationLabel = (minutes) => {
    if (minutes < 60) {
      return `${minutes} min`;
    } else if (minutes === 1440) {
      return `24 hr`;
    } else if (minutes < 1440) {
      const hours = minutes / 60;
      return `${hours} h`;
    } else {
      const days = minutes / 1440;
      return `${days} d`;
    }
  };

  const formatTooltipLabel = (value) => {
  return t("idf.chart.tooltipDuration", { value: formatDurationLabel(value) });
  };
  const isUsSelected = selectedCountry === "US";
  const effectiveUsDataType = usCurveMeta?.dataType || usDataType;
  const effectiveUsSeries = usCurveMeta?.series || usSeries;
  const effectiveUsEstimate = usCurveMeta?.estimate || usEstimate;
  const effectiveUsUnits =
    effectiveUsDataType === "depth"
      ? t("idf.country.usForm.units.depth")
      : t("idf.country.usForm.units.intensity");
  const yAxisLabel = isUsSelected
    ? effectiveUsDataType === "depth"
      ? t("idf.chart.depthAxisUs")
      : t("idf.chart.intensityAxisUs")
    : t("idf.chart.intensityAxis");

  const buildNoaaVerifyUrl = useCallback((lat, lon, dataType, series) => {
    const params = new URLSearchParams({
      lat: Number(lat).toFixed(6),
      lon: Number(lon).toFixed(6),
      data: dataType === "depth" ? "depth" : "intensity",
      units: "english",
      series: series === "pds" ? "pds" : "ams",
    });
    return `https://hdsc.nws.noaa.gov/pfds/pfds_printpage.html?${params.toString()}`;
  }, []);
  
  const yAxisDomain = useMemo(() => {
    if (!idfData || idfData.length === 0) {
       return [0.1, "auto"];
    }
    let maxValue = 0;
    let minPositive = Number.POSITIVE_INFINITY;
    idfData.forEach((item) => {
      allReturnPeriods.forEach((period) => {
        const curveValue = Number(item[period]);
        if (!Number.isFinite(curveValue) || curveValue <= 0) return;
        if (curveValue > maxValue) {
          maxValue = curveValue;
        }
        if (curveValue < minPositive) {
          minPositive = curveValue;
        }
      });
    });
    if (!Number.isFinite(minPositive) || maxValue <= 0) {
      return [0.1, "auto"];
    }
    const lowerLimit = minPositive < 1 ? Math.max(minPositive * 0.8, 0.01) : 1;
    const upperLimit = Math.max(maxValue * 1.2, lowerLimit * 10);
    return [lowerLimit, upperLimit];
  }, [idfData]);
  const formatTooltipValue = useCallback(
    (value) => {
      if (value == null || !Number.isFinite(Number(value))) return "-";
      return `${Number(value).toFixed(isUsSelected ? 2 : 1)} ${
        isUsSelected ? effectiveUsUnits : "mm/h"
      }`;
    },
    [effectiveUsUnits, isUsSelected],
  );
  const isTrialPending = (user?.subscriptionStatus || "").toLowerCase() === "trial_pending";
  const bannerMessage = trialExpired
    ? t("idf.trial.expired")
    : trialMessage || (isTrialPending ? t("idf.trial.pendingVerification") : "");
  const handleStartTrialVerification = useCallback(async () => {
    if (!user) {
      window.location.assign("/login");
      return;
    }
    if (!authFetch) {
      setError(t("idf.trial.startFailed"));
      return;
    }

    const confirmed = window.confirm(t("idf.trial.pendingConsentConfirm"));
    if (!confirmed) return;

    try {
      setTrialVerificationLoading(true);
      setError(null);
      const res = await authFetch(buildApiUrl("/billing/create-trial-verification-session"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ autoRenewConsent: true }),
      });
      const data = await readJsonResponse(res, t("idf.trial.startFailed"));
      if (!res.ok) {
        throw new Error(data?.error || data?.message || t("idf.trial.startFailed"));
      }
      if (!data?.url) {
        throw new Error(t("idf.trial.startFailed"));
      }
      window.location.assign(data.url);
    } catch (err) {
      setError(err?.message || t("idf.trial.startFailed"));
    } finally {
      setTrialVerificationLoading(false);
    }
  }, [authFetch, setError, t, user]);

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto p-6 mt-10 border border-yellow-400 bg-yellow-100 rounded text-center text-yellow-900">
        <p className="mb-4 text-lg font-semibold">
          {t("idf.authPrompt.prefix")}{" "}
          <Link to="/login" className="text-blue-700 underline">
            {t("idf.authPrompt.link")}
          </Link>{" "}
          {t("idf.authPrompt.suffix")}
        </p>
      </div>
    );
  }
 
  // No explicit "Loading Maps" gate; Autocomplete component handles script loading.

  return (
    <div className="bg-gray-50 min-h-screen flex flex-col items-center justify-center p-4 sm:p-6 lg:p-8 font-sans">
        {!OPEN_ACCESS_MODE && (bannerMessage || trialExpired || isTrialPending) && (
          <div className="w-full max-w-5xl mb-4">
            <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg shadow-sm text-sm space-y-3">
            <p>
              {bannerMessage}
            </p>
              {trialExpired && (
                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition"
                  >
                    {t("idf.trial.contactUpgrade")}
                  </Link>
                  <a
                    href="mailto:support@civispec.com"
                    className="inline-flex items-center justify-center rounded-md border border-indigo-600 px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 transition"
                  >
                    {t("idf.trial.emailSupport")}
                  </a>
                </div>
              )}
              {isTrialPending && (
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={handleStartTrialVerification}
                    disabled={trialVerificationLoading}
                    className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {trialVerificationLoading ? t("idf.trial.verifying") : t("idf.trial.verifyNow")}
                  </button>
                  <Link
                    to="/#pricing"
                    className="inline-flex items-center justify-center rounded-md border border-indigo-600 px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 transition"
                  >
                    {t("idf.trial.viewPricing")}
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      <div className="w-full max-w-5xl bg-white p-6 sm:p-8 lg:p-10 rounded-2xl shadow-xl flex flex-col md:flex-row gap-6">
        {/* Left Side: Search and Controls */}
        <div className="w-full md:w-1/3 space-y-6">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold text-gray-800">{t("idf.viewer.title")}</h1>
            <MapPinIcon className="text-gray-600 text-3xl" />
          </div>

          <p className="text-gray-600">
             {t("idf.viewer.intro")}
          </p>

          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
            <label className="block text-sm font-medium text-gray-700">
              {t("idf.country.label")}
            </label>
            <select
              value={selectedCountry}
              onChange={(e) => {
                const nextCountry = e.target.value;
                setSelectedCountry(nextCountry);
                setIDFData([]);
                chartDataRef.current = null;
                setLoading(false);
                setUsLoading(false);
                setShowChart(false);
                setStation(null);
                setIsStationInfoVisible(false);
                setClimateInfo(null);
                setIdfFallbackInfo(null);
                setUsLocationQuery("");
                setUsLatitude("");
                setUsLongitude("");
                setUsProviderMessage("");
                setUsResolvedLocation(null);
                setUsDataType("intensity");
                setUsSeries("ams");
                setUsEstimate("mean");
                setUsCurveMeta({
                  dataType: "intensity",
                  series: "ams",
                  estimate: "mean",
                });
                setError(null);
              }}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="CA">{t("idf.country.options.ca")}</option>
              <option value="US">{t("idf.country.options.us")}</option>
            </select>
          </div>

          {selectedCountry === "CA" ? (
          <>
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={applyClimate2050High}
                onChange={(e) => {
                  const next = e.target.checked;
                  setApplyClimate2050High(next);
                  if (next) setApplyQc18(false);
                }}
                className="mt-1"
              />
              <span>
                {t("idf.climate.canadaToggleLabel")}{" "}
                  <span className="font-semibold">{t("idf.climate.canadaToggleValue")}</span>
              </span>
            </label>
            <p className="mt-2 text-xs text-gray-500">
              {t("idf.climate.canadaHint")}
            </p>
            {applyClimate2050High && (
              <div className="mt-2 text-xs">
                {climateInfo?.applied ? (
                  <div className="text-emerald-700">
                {t("idf.climate.appliedLabel")}{" "}
                <span className="font-semibold">{t("idf.climate.yes")}</span>
                {climateInfo?.modelsUsed
                  ? ` (${t("idf.climate.models")}: ${climateInfo.modelsUsed})`
                  : ""}
              </div>
                ) : (
                 <div className="text-amber-700">
                  {t("idf.climate.appliedLabel")}{" "}
                  <span className="font-semibold">{t("idf.climate.no")}</span>
                  {climateInfo?.reason ? ` — ${climateInfo.reason}` : ""}
                </div>
                )}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={applyQc18}
                onChange={(e) => {
                  const next = e.target.checked;
                  setApplyQc18(next);
                  if (next) setApplyClimate2050High(false);
                }}
                className="mt-1"
              />
              <span>
                {t("idf.climate.qcToggleLabel")}{" "}
                <span className="font-semibold">{t("idf.climate.qcToggleValue")}</span>
              </span>
            </label>
            <p className="mt-2 text-xs text-gray-500">
              {t("idf.climate.qcHint")}
            </p>
            {applyQc18 && (
            <div className="mt-2 text-xs">
              {climateInfo?.applied ? (
                <div className="text-emerald-700">
                  {t("idf.climate.appliedLabel")}{" "}
                  <span className="font-semibold">{t("idf.climate.yes")}</span>{" "}
                  ({t("idf.climate.factor")}: 1.18)
                </div>
              ) : (
                <div className="text-amber-700">
                  {t("idf.climate.appliedLabel")}{" "}
                  <span className="font-semibold">{t("idf.climate.no")}</span>
                  {climateInfo?.reason ? ` — ${climateInfo.reason}` : ""}
                </div>
              )}
            </div>
          )}
          </div>

          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={allowSubhourFallback}
                onChange={(e) => setAllowSubhourFallback(e.target.checked)}
                className="mt-1"
              />
              <span>
                {t("idf.fallback.toggleLabelPrefix")}{" "}
                <span className="font-semibold">{t("idf.fallback.toggleLabelEmphasis")}</span>{" "}
                {t("idf.fallback.toggleLabelSuffix")}
              </span>
            </label>
            <p className="mt-2 text-xs text-gray-500">
              {t("idf.fallback.hint")}
            </p>
            {idfFallbackInfo?.shortDurationFallback && (
              <div className="mt-2 text-xs text-amber-700">
                {t("idf.fallback.usingFrom")}{" "}
                <span className="font-semibold">
                  {idfFallbackInfo.shortDurationUsedStationName || t("idf.fallback.nearestStation")}
                </span>
                {idfFallbackInfo.shortDurationUsedStationId
                  ? `(${idfFallbackInfo.shortDurationUsedStationId})`
                  : ""}
                {typeof idfFallbackInfo.shortDurationDistanceKm === "number"
                  ? ` — ${idfFallbackInfo.shortDurationDistanceKm} km`
                  : ""}
                .
              </div>
            )}
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
          <p className="mb-2 font-medium">{t("idf.search.modeLabel")}</p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSearchMode("nearest")}
              className={`px-3 py-1 rounded-md border ${
                searchMode === "nearest"
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-white text-gray-700 border-gray-300"
              }`}
            >
              {t("idf.search.modeNearest")}
            </button>

            <button
              type="button"
              onClick={() => setSearchMode("direct")}
              className={`px-3 py-1 rounded-md border ${
                searchMode === "direct"
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-white text-gray-700 border-gray-300"
              }`}
            >
              {t("idf.search.modeDirect")}
            </button>
          </div>
          {searchMode === "direct" && (
          <p className="mt-2 text-xs text-gray-500">
            {stationsLoading
              ? "Loading stations..."
              : stationsError
              ? stationsError
              : `${stations.length} stations loaded`}
          </p>
          )}
        </div>
        {searchMode === "nearest" && (    
          <form onSubmit={handleSearch} className="space-y-4">
             <div>
                <label
                  htmlFor="location"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t("idf.search.locationLabel")}
                </label>
                <Autocomplete
                  apiKey={GOOGLE_MAPS_API_KEY}
                  onPlaceSelected={(selectedPlace) => {
                    if (!selectedPlace || !selectedPlace.geometry) return;
                    setPlace(selectedPlace);
                  }}
                  options={{
                    types: ["(cities)"],
                    componentRestrictions: { country: "ca" },
                  }}
                  id="location"
                  name="location"
                  placeholder={t("idf.search.locationPlaceholder")}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>
            <button
              type="submit"
              className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading || !HAS_GOOGLE_API_KEY}
            >
              {loading ? (
                <div className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>{t("idf.search.searching")}</span>
                </div>
              ) : (
                <>
                  <SearchIcon className="mr-2 h-5 w-5" />
                  <span>{t("idf.search.findStation")}</span>
                </>
              )}
            </button>
          </form>
          )}
          {searchMode === "direct" && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t("idf.search.direct.provinceLabel")}
              </label>
              <select
                value={selectedProvince}
                onChange={(e) => {
                  setSelectedProvince(e.target.value);
                  setStationMenuOpen(true);
                  setHighlightedIndex(0);
                }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                {provinceOptions.map((prov) => (
                  <option key={prov} value={prov}>
                    {prov === "ALL" ? t("idf.search.direct.allProvinces") : prov}
                  </option>
                ))}
              </select>
            </div>
            <label className="block text-sm font-medium text-gray-700">
              {t("idf.search.direct.stationLabel")}
            </label>

            <div className="relative">
              <input
                type="text"
                value={stationQuery}
                onChange={(e) => handleStationQueryChange(e.target.value)}
                onFocus={() => setStationMenuOpen(true)}
                onKeyDown={handleStationInputKeyDown}
                placeholder={t("idf.search.direct.stationPlaceholder")}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />

              {stationMenuOpen && stationOptions.length > 0 && (
                <div className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
                  {stationOptions.map((station, idx) => (
                    <button
                      key={`${station.stationId}-${idx}`}
                      type="button"
                      onClick={() => handleStationSelect(station)}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                        idx === highlightedIndex ? "bg-indigo-50" : ""
                      }`}
                    >
                      {formatStationLabel(station)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {selectedStation && (
              <p className="text-xs text-emerald-700">
                {t("idf.search.direct.selectedPrefix")} {formatStationLabel(selectedStation)}
              </p>
            )}

            <button
              type="button"
              onClick={handleDirectLoad}
              disabled={loading || !selectedStation}
              className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t("idf.search.direct.loadingButton") : t("idf.search.direct.loadButton")}
            </button>
          </div>
        )}
          {error && (
            <div
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg relative"
              role="alert"
            >
              <span className="block sm:inline">{error}</span>
            </div>
          )}

          {isStationInfoVisible && station && (
            <div className="bg-gray-100 p-4 rounded-md mt-4 shadow">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold text-gray-700">{t("idf.station.title")}</h3>
                <button onClick={() => setIsStationInfoVisible(false)}>
                  <CloseIcon className="h-4 w-4 text-gray-500 hover:text-gray-700" />
                </button>
              </div>
              <p className="text-gray-600">
                 <strong className="text-gray-800">{t("idf.station.id")}</strong>{" "}
                {station.stationId}
              </p>
              <p className="text-gray-600">
                 <strong className="text-gray-800">{t("idf.station.name")}</strong>{" "}
                {station.stationName || station.name || ""}
              </p>
              <p className="text-gray-600">
                 <strong className="text-gray-800">{t("idf.station.latitude")}</strong>{" "}
                {station.lat}
              </p>
              <p className="text-gray-600">
                <strong className="text-gray-800">{t("idf.station.longitude")}</strong>{" "}
                {station.lon}
              </p>
                <p className="text-gray-600">
                <strong className="text-gray-800">{t("idf.station.distance")}</strong>{" "}
                {station.distance_km} km
              </p>
            </div>
          )}
          </>
          ) : (
            <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <p className="text-xs">{t("idf.country.usForm.helper")}</p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <div>
                  <label className="block text-xs font-semibold text-amber-900">
                    {t("idf.country.usForm.dataTypeLabel")}
                  </label>
                  <select
                    value={usDataType}
                    onChange={(e) => setUsDataType(e.target.value)}
                    className="mt-1 block w-full rounded-md border-amber-300 bg-white text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value="intensity">{t("idf.country.usForm.dataType.intensity")}</option>
                    <option value="depth">{t("idf.country.usForm.dataType.depth")}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-amber-900">
                    {t("idf.country.usForm.seriesLabel")}
                  </label>
                  <select
                    value={usSeries}
                    onChange={(e) => setUsSeries(e.target.value)}
                    className="mt-1 block w-full rounded-md border-amber-300 bg-white text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value="ams">{t("idf.country.usForm.series.ams")}</option>
                    <option value="pds">{t("idf.country.usForm.series.pds")}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-amber-900">
                    {t("idf.country.usForm.estimateLabel")}
                  </label>
                  <select
                    value={usEstimate}
                    onChange={(e) => setUsEstimate(e.target.value)}
                    className="mt-1 block w-full rounded-md border-amber-300 bg-white text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value="mean">{t("idf.country.usForm.estimate.mean")}</option>
                    <option value="upper">{t("idf.country.usForm.estimate.upper")}</option>
                    <option value="lower">{t("idf.country.usForm.estimate.lower")}</option>
                  </select>
                </div>
              </div>
              <p className="text-xs font-medium">
                {t("idf.country.usForm.unitsNote", {
                  units:
                    usDataType === "depth"
                      ? t("idf.country.usForm.units.depth")
                      : t("idf.country.usForm.units.intensity"),
                })}
              </p>
              <div>
                <label className="block text-sm font-medium text-amber-900">
                  {t("idf.country.usForm.placeLabel")}
                </label>
                {hasGoogleApiKey ? (
                  <Autocomplete
                    apiKey={GOOGLE_MAPS_API_KEY}
                    onPlaceSelected={(selectedPlace) => {
                      const placeLabel =
                        selectedPlace?.formatted_address || selectedPlace?.name || "";
                      if (placeLabel) setUsLocationQuery(placeLabel);

                      if (selectedPlace?.geometry?.location) {
                        const placeLat = selectedPlace.geometry.location.lat();
                        const placeLon = selectedPlace.geometry.location.lng();
                        setUsLatitude(String(Number(placeLat).toFixed(6)));
                        setUsLongitude(String(Number(placeLon).toFixed(6)));
                        setUsProviderMessage(t("idf.country.usForm.autofilled"));
                      }
                    }}
                    onChange={(e) => {
                      const value = e.target.value;
                      setUsLocationQuery(value);
                      setUsLatitude("");
                      setUsLongitude("");
                      setUsResolvedLocation(null);
                    }}
                    options={{
                      types: ["(regions)"],
                      componentRestrictions: { country: "us" },
                    }}
                    id="us-location"
                    name="us-location"
                    placeholder={t("idf.country.usForm.placePlaceholder")}
                    className="mt-1 block w-full rounded-md border-amber-300 bg-white text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                ) : (
                  <input
                    type="text"
                    value={usLocationQuery}
                    onChange={(e) => {
                      const value = e.target.value;
                      setUsLocationQuery(value);
                      setUsLatitude("");
                      setUsLongitude("");
                      setUsResolvedLocation(null);
                    }}
                    placeholder={t("idf.country.usForm.placePlaceholder")}
                    className="mt-1 block w-full rounded-md border-amber-300 bg-white text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                )}
                <p className="mt-1 text-xs text-amber-700">
                  {t("idf.country.usForm.placeHint")}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-amber-900">
                  {t("idf.country.usForm.latitudeLabel")}
                </label>
                <input
                  type="number"
                  step="any"
                  value={usLatitude}
                  onChange={(e) => setUsLatitude(e.target.value)}
                  placeholder={t("idf.country.usForm.latitudePlaceholder")}
                  className="mt-1 block w-full rounded-md border-amber-300 bg-white text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-amber-900">
                  {t("idf.country.usForm.longitudeLabel")}
                </label>
                <input
                  type="number"
                  step="any"
                  value={usLongitude}
                  onChange={(e) => setUsLongitude(e.target.value)}
                  placeholder={t("idf.country.usForm.longitudePlaceholder")}
                  className="mt-1 block w-full rounded-md border-amber-300 bg-white text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>
              <button
                type="button"
                onClick={handleUsLoad}
                disabled={usLoading}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {usLoading
                  ? t("idf.country.usForm.loadingButton")
                  : t("idf.country.usForm.loadButton")}
              </button>
              {usResolvedLocation && (
                <div className="rounded-md border border-amber-300 bg-white px-3 py-2 text-sm text-amber-800">
                  <div className="font-medium">
                    {t("idf.country.usForm.locationLabel")}: {usResolvedLocation.label}
                  </div>
                  {Number.isFinite(Number(usResolvedLocation.lat)) &&
                    Number.isFinite(Number(usResolvedLocation.lon)) && (
                      <>
                        <div className="mt-1 text-xs">
                          {t("idf.country.usForm.usedCoordinates", {
                            lat: Number(usResolvedLocation.lat).toFixed(4),
                            lon: Number(usResolvedLocation.lon).toFixed(4),
                          })}
                        </div>
                        <a
                          href={buildNoaaVerifyUrl(
                            usResolvedLocation.lat,
                            usResolvedLocation.lon,
                            effectiveUsDataType,
                            effectiveUsSeries,
                          )}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-1 inline-block text-xs text-indigo-700 underline hover:text-indigo-900"
                        >
                          {t("idf.country.usForm.verifyLink")}
                        </a>
                        <div className="mt-1 text-xs text-amber-700">
                          {t("idf.country.usForm.modeSummary", {
                            dataType: t(`idf.country.usForm.dataType.${effectiveUsDataType}`),
                            series: t(`idf.country.usForm.series.${effectiveUsSeries}`),
                            estimate: t(`idf.country.usForm.estimate.${effectiveUsEstimate}`),
                            units: effectiveUsUnits,
                          })}
                        </div>
                      </>
                    )}
                </div>
              )}
              <div className="rounded-md border border-amber-300 bg-white px-3 py-2 text-sm text-amber-800">
                {usLoading ? t("idf.country.checking") : usProviderMessage || t("idf.country.usNeedsCoordinates")}
              </div>
              {error && (
                <div
                  className="bg-red-100 border border-red-300 text-red-700 px-3 py-2 rounded-lg"
                  role="alert"
                >
                  <span className="block sm:inline">{error}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side: Chart and Checkbox Controls. Hidden until a search is successful. */}
        {showChart && (
          <div className="w-full md:w-2/3 space-y-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-800">{t("idf.chart.title")}</h2>
                {isUsSelected && (
                  <p className="text-xs text-gray-500">
                    {t("idf.country.usForm.modeSummary", {
                      dataType: t(`idf.country.usForm.dataType.${effectiveUsDataType}`),
                      series: t(`idf.country.usForm.series.${effectiveUsSeries}`),
                      estimate: t(`idf.country.usForm.estimate.${effectiveUsEstimate}`),
                      units: effectiveUsUnits,
                    })}
                  </p>
                )}
              </div>
              <div className="relative" ref={exportMenuRef}>
                <button
                  type="button"
                  disabled={!idfData.length}
                  title={!idfData.length ? t("idf.export.emptyHint") : ""}
                  onClick={() => setExportOpen((v) => !v)}
                  className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <DownloadIcon className="mr-2 h-4 w-4" />
                  {t("idf.export.button")}
                  <span className="ml-2 text-xs">▾</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handlePdfDownload();
                    setExportOpen(false);
                  }}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50"
                >
                  <div className="text-sm font-semibold">{t("idf.export.items.pdf.label")}</div>
                  <div className="text-[11px] text-gray-500">{t("idf.export.items.pdf.hint")}</div>
                </button>  
                {exportOpen && !!idfData.length && (
                  <div className="absolute right-0 z-30 mt-2 w-64 max-w-[90vw] overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg">
                    <button
                      type="button"
                      onClick={() => {
                        handleCSVDownload();
                        setExportOpen(false);
                      }}
                      className="w-full px-3 py-2 text-left hover:bg-gray-50"
                    >
                      <div className="text-sm font-semibold">{t("idf.export.items.csv.label")}</div>
                      <div className="text-[11px] text-gray-500">{t("idf.export.items.csv.hint")}</div>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        handleGeoJSONDownload();
                        setExportOpen(false);
                      }}
                      className="w-full px-3 py-2 text-left hover:bg-gray-50"
                    >
                      <div className="text-sm font-semibold">{t("idf.export.items.geojson.label")}</div>
                      <div className="text-[11px] text-gray-500">{t("idf.export.items.geojson.hint")}</div>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        handleDownload();
                        setExportOpen(false);
                      }}
                      className="w-full px-3 py-2 text-left hover:bg-gray-50"
                    >
                      <div className="text-sm font-semibold">{t("idf.export.items.json.label")}</div>
                      <div className="text-[11px] text-gray-500">{t("idf.export.items.json.hint")}</div>
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-4 mb-4">
              {allReturnPeriods.map((period) => (
                <label key={period} className="inline-flex items-center">
                  <input
                    type="checkbox"
                    value={period}
                    checked={selectedReturnPeriods.includes(period)}
                    onChange={handleCheckboxChange}
                    className="rounded-md text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    {t("idf.chart.returnPeriod", { period })}
                  </span>
                </label>
              ))}
            </div>

            <div className="bg-gray-50 p-4 rounded-lg shadow-inner">
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={idfData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="duration"
                    label={{
                      value: t("idf.chart.durationAxis"),
                      position: "insideBottom",
                      offset: -5,
                    }}
                    tick={{ fontSize: 12 }}
                    scale="log"
                    ticks={[1, 2, 5, 10, 15, 30, 60, 120, 360, 720, 1440]}
                    tickFormatter={formatDurationLabel}
                    domain={["dataMin", "dataMax"]}
                  />
                  <YAxis
                    label={{
                      value: yAxisLabel,
                      angle: -90,
                      position: "insideLeft",
                      offset: 15,
                    }}
                    tick={{ fontSize: 12 }}
                    scale="log"
                    domain={yAxisDomain}
                  />
                  <Tooltip labelFormatter={formatTooltipLabel} formatter={formatTooltipValue} />
                  <Legend verticalAlign="bottom" height={36} />
                  {allReturnPeriods.map(
                    (period) =>
                      selectedReturnPeriods.includes(period) && (
                        <Line
                          key={period}
                          type="monotone"
                          dataKey={period}
                          stroke={getLineColor(period)}
                          strokeWidth={2}
                          dot={false}
                          strokeDasharray={getLineDash(period)}
                          isAnimationActive={false}
                          name={t("idf.chart.legend", { period })}
                        />
                      ),
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white rounded-lg shadow-md overflow-x-auto mt-6">
              <table className="min-w-full text-sm text-left text-gray-700 border">
                <thead className="bg-gray-100 text-xs uppercase text-gray-600">
                  <tr>
                    <th className="px-4 py-2 border">{t("idf.chart.tableDuration")}</th>
                    {allReturnPeriods.map((period) => (
                      <th key={period} className="px-4 py-2 border">
                        {t("idf.chart.legend", { period })}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {idfData.map((row, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                       <td className="px-4 py-2 border">
                        {formatDurationLabel(row.duration)}
                      </td>
                      {allReturnPeriods.map((period) => (
                        <td key={period} className="px-4 py-2 border">
                          {formatIntensityValue(row[period])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MVPIDFViewerV2;

